import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { Store } from '@ngxs/store';

import { BillingAddressModalComponent, GiftShareValidators, NAME_REGEXP } from '../../shared';
import { environment } from '../../../environments/environment';
import {
  AccountService, ContributionService, EventService,
  GtagService, SeoService, ToastService, ValidationService
} from '../../core/services';
import { AuthState, LocalActions } from '../../state';
import { AddressType, BillingAddress, City, UserProfile } from '../../core/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { eventImage } from '../../core/helpers';

@Component({
  selector: 'gs-contribute',
  templateUrl: './contribute.component.html',
  standalone: false
})
export class ContributeComponent implements OnInit, OnDestroy {

  tabs = {
    ACCOUNT: 0,
    CONTRIBUTION: 1,
    CONFIRMATION: 2
  };

  selectedTab: number = this.tabs.ACCOUNT;
  firstTab: number = this.tabs.ACCOUNT;

  // Current loggedin user
  user: UserProfile;
  // User created as part of the contribution process
  registeredUser: UserProfile;

  event: any;
  eventCode: string;

  isRegistrationOpen: boolean;
  newUserBonus: number = 0;
  cities: City[];

  form: FormGroup = this.formBuilder.group({
    amount: [null, [Validators.required]],
    note: [null, [Validators.maxLength(500)]],
    isAnonymous: [false, [Validators.required]],
    address: [null, [Validators.required]],
  });

  // Register
  formRegister: FormGroup = this.formBuilder.group({
    firstName: [null, [Validators.required, Validators.maxLength(50),
    Validators.pattern(NAME_REGEXP)]],
    lastName: [null, [Validators.required, Validators.maxLength(50),
    Validators.pattern(NAME_REGEXP)]],
    email: [null, [Validators.required,
    GiftShareValidators.email,
    Validators.maxLength(256)]],
    cityId: [null, [Validators.required]],
    canRegisterAccount: [false, [Validators.required]],
  });

  formConfirmation: FormGroup = this.formBuilder.group({
    hasAcceptedTerms: [false, [Validators.requiredTrue]],
  });

  private contributionMinimum: number;
  private contributionMaximum: number;
  private hasContributionsEnabled: boolean;

  private subscriptions: Subscription[] = [];

  get canContribute(): boolean {
    return this.hasContributionsEnabled && !this.event.isSuspended && this.event.isStarted && this.event.status == 'Active';
  }

  get reason(): string {
    return this.event.status != 'Active' ? `Ne pare rau, acest eveniment a expirat si nu poate primi contributii!`
      : this.event.isSuspended ? `Evenimentul a fost suspendat de catre administrator! Motiv: ${this.event.suspendReason}`
        : !this.event.isStarted ? 'Evenimentul a fost oprit temporar.'
          : '';
  }

  get nextLabel(): string {
    return this.selectedTab == this.tabs.CONFIRMATION ? 'Contribuie' : 'Inainte';
  }

  get backLabel(): string {
    return this.selectedTab == this.firstTab ? 'Anuleaza' : 'Inapoi';
  }

  get address(): BillingAddress {
    return this.form.value.address;
  }

  get isCompanyAddress(): boolean {
    return this.form.value.address
      && this.form.value.address.type === AddressType.Company;
  }

  get canDisplayAccountStep(): boolean {
    return !this.user;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private eventService: EventService,
    private contributionService: ContributionService,
    private gtagService: GtagService,
    private accountService: AccountService,
    private modalService: NgbModal,
    private store: Store,
    private formBuilder: FormBuilder,
    public vs: ValidationService,
    private viewportScroller: ViewportScroller,
    private seoService: SeoService,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
          if (this.user) {
            this.firstTab = this.selectedTab = this.tabs.CONTRIBUTION;
          }
          if (!this.address) {
            const defaultAddress = this.user?.addresses.find(x => x.isDefault);
            if (defaultAddress) {
              const { id, ...addressRest } = defaultAddress;
              this.form.patchValue({
                address: { ...addressRest, addressId: defaultAddress.id }
              });
            }
          }
        }),

      this.store.select<any>(state => state.staticData.publicSettings)
        .pipe(filter(x => !!x))
        .subscribe((settings: any) => {
          this.hasContributionsEnabled = settings.contribution;
          this.isRegistrationOpen = settings.registration;
          this.newUserBonus = settings.newUserBonus;
          this.contributionMaximum = settings.contributionMaximum;
          this.contributionMinimum = settings.contributionMinimum;

          this.form.controls.amount.setValidators([Validators.required, Validators.min(this.contributionMinimum), Validators.max(this.contributionMaximum)]);
        }),

      this.store.select<City[]>(state => state.staticData.cities)
        .pipe(filter(x => !!x))
        .subscribe(cities => {
          this.cities = cities;
        })
    );

    this.eventCode = this.route.snapshot.paramMap.get('eventCode');

    this.getEvent();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private getEvent() {
    if (this.store.selectSnapshot(AuthState.isCorporateUser)) {
      this.toastService.show('Eroare', 'Utilizatorul corporativ nu poate contribui la evenimentele membrilor', 'error');
      this.router.navigate(['/evenimente-publice']);
      return;
    }

    this.subscriptions.push(
      this.eventService.getPublicEventByCode(this.eventCode)
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => { })
        )
        .subscribe((event: any) => {
          this.event = event;

          if (event.hasSuggestedAmount) {
            this.form.controls.amount.patchValue(event.suggestedAmount);
          }

          // Custom SEO tags
          this.seoService.setTitle(`Contribuie la "${this.event.name}"`);
          this.seoService.setDescription(this.event.description);
          this.seoService.setImage(eventImage(this.event));
        })
    );

  }

  onTabSelected(id: number) {
    this.selectedTab = id;
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  onBackClick() {
    if (this.selectedTab > this.firstTab) {
      this.onTabSelected(this.selectedTab - 1);
    } else {
      this.router.navigate(['/eveniment', this.eventCode]);
    }
  }

  onNextClick() {
    switch (this.selectedTab) {
      case this.tabs.ACCOUNT: {

        if (!this.formRegister.valid) {
          this.vs.triggerValidation(this.formRegister);
          return;
        }

        const callback = () => {

          // Automatically fill the address with the user's data
          if (!this.address) {
            this.form.patchValue({
              address: {
                type: AddressType.Personal,
                firstName: this.formRegister.value.firstName,
                lastName: this.formRegister.value.lastName,
                cityId: this.formRegister.value.cityId,
              }
            });
          }
          this.onTabSelected(this.tabs.CONTRIBUTION);
        };

        if (this.formRegister.value.canRegisterAccount) {
          this.register(callback);
        } else {
          callback();
        }

        break;
      }
      case this.tabs.CONTRIBUTION: {
        if (!this.form.valid) {
          this.vs.triggerValidation(this.form);
          return;
        }

        this.onTabSelected(this.tabs.CONFIRMATION);

        break;
      }
      case this.tabs.CONFIRMATION: {
        if (!this.formConfirmation.valid) {
          this.vs.triggerValidation(this.formConfirmation);
          return;
        }

        this.createContribution({
          ...this.form.value,
          userId: this.user?.id || this.registeredUser?.id,
          eventId: this.event.id,
          firstName: (this.user ? this.user.firstName : this.formRegister.value.firstName),
          lastName: (this.user ? this.user.lastName : this.formRegister.value.lastName),
          email: (this.user ? this.user.email : this.formRegister.value.email),
          cityId: (this.user ? this.user.cityId : this.formRegister.value.cityId),
        });

        break;
      }
    }
  }

  private register(onSuccess) {

    const model = {
      ...this.formRegister.value
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.accountService.registerOnContribution(model)
        .pipe(
          catchError(e => {
            //this.onTabSelected(this.tabs.CONTRIBUTION); //It is going to the contribution only on success
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe((response: any) => {
          if (environment.googleAnalytics?.conversionGroups?.registration) {
            this.gtagService.trackConversion(environment.googleAnalytics.conversionGroups.registration);
          }

          this.registeredUser = response;

          // Disable the register checkbox to avoid re-registering if the user goes back
          this.formRegister.patchValue({
            canRegisterAccount: false
          });

          this.formRegister.controls.canRegisterAccount.disable();

          if (onSuccess && typeof onSuccess == 'function') {
            onSuccess();
          }
        })
    );
  }

  private createContribution(contribution) {
    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.contributionService.createContribution(contribution)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe((response: any) => {
          this.store.dispatch(new LocalActions.SetLastContribution(response.code));
          this.document.location.href = response.clientRedirectUrl
        })
    );
  }

  getCity(cityId: number): City {
    return this.cities?.find(x => x.id === cityId);
  }

  onEditAddress(address: BillingAddress = null) {
    address = address ?? { type: AddressType.Personal } as BillingAddress;

    const modalRef = this.modalService.open(BillingAddressModalComponent, { backdrop: 'static', size: 'lg' });
    modalRef.componentInstance.address = address;

    this.subscriptions.push(
      modalRef.closed
        .subscribe((result: BillingAddress) => {
          if (result) {
            this.form.patchValue({
              address: { ...result }
            })
          }
        })
    );
  }
}
